// services/CoachSeverityEngine.ts
import type {
  ActionableAdvice,
  AnalysisResult,
  DifficultyLevel,
  DiagnosticExplanation,
  MotorPrescription
} from "../types";

import { getTimingToleranceMs, GLOBAL_SEVERITY_MATRIX } from "./severityEngine";
import { getVisibleTabsForLevel } from "./viewPolicy";
import { buildMotorPrescription } from "./motorPriorityPolicy";

export interface CoachingVerdict {
  severity: string;
  tone: "BENEVOLENT" | "STANDARD" | "STRICT" | "ELITE";
  prioritizedAdvices: string[];
  diagnostics: ActionableAdvice[];
  explanations?: DiagnosticExplanation[];
  primaryPrescription?: MotorPrescription;
}

/* ------------------------------------------------------------------
   HELPERS
------------------------------------------------------------------- */

function safeMinutes(durationSec: number): number {
  const d = Number.isFinite(durationSec) ? durationSec : 0;
  return Math.max(0.01, d / 60);
}

function ghostsPerMinute(result: AnalysisResult): number {
  const ghosts = (result as any)?.tone?.ghostNotes?.length ?? 0;
  const mins = safeMinutes(result.session.duration || 0);
  return ghosts / mins;
}

function cleanlinessThreshold(levelId: number): number {
  return (
    GLOBAL_SEVERITY_MATRIX?.[levelId]?.cleanliness?.maxGhostsPerMin ??
    25 // fallback sûr
  );
}

/* ------------------------------------------------------------------
   1) DIAGNOSTIC GENERATORS (NE PAS CASSER)
------------------------------------------------------------------- */

function generateTimingAdvice(result: AnalysisResult, level: DifficultyLevel, isFr: boolean): ActionableAdvice[] {
  const advices: ActionableAdvice[] = [];
  const metrics = result.session.metrics;

  const deviations = (((metrics.debug as any)?.timingDeviationMs as number[] | undefined) || []);
  const bpm = result.session.bpm || 90;
  const toleranceMs = getTimingToleranceMs(level.id, bpm);

  if (deviations.length < 4) return advices;

  const avg = deviations.reduce((a, b) => a + b, 0) / deviations.length;

  if (Math.abs(avg) > toleranceMs) {
    const isRushing = avg < 0;
    advices.push({
      domain: "TIMING",
      severity: "CRITICAL",
      title: isFr ? (isRushing ? "Le Balancier s'emballe" : "Retrouver l'Assise") : (isRushing ? "Rushing the Pulse" : "Finding the Ground"),
      instruction: isFr
        ? (isRushing
          ? "Votre mouvement est trop nerveux. Respirez et laissez tomber votre main droite avec plus d'inertie."
          : "Votre geste manque d'élan. Visualisez le balancier d'une horloge qui ne ralentit jamais.")
        : (isRushing
          ? "Your movement is too tense. Breathe and let your right hand drop with more momentum."
          : "Your gesture lacks swing. Visualize a clock pendulum."),
      drill_hint: isFr ? (isRushing ? "Action : Calmez le balancier" : "Action : Anticipez l'impact") : (isRushing ? "Action: Calm the swing" : "Action: Anticipate impact"),
      icon: isRushing ? "🐇" : "🐢"
    });
  } else {
    advices.push({
      domain: "TIMING",
      severity: "SUCCESS",
      title: isFr ? "Pulsation Ancrée" : "Grounded Pulse",
      instruction: isFr ? "Votre pulsation est naturelle. On sent une intention stable." : "Your pulse is natural. A steady intent is felt.",
      drill_hint: isFr ? "Prêt pour un défi de vélocité" : "Ready for a velocity challenge",
      icon: "✨"
    });
  }

  return advices;
}

function generateAttackAdvice(result: AnalysisResult, _level: DifficultyLevel, isFr: boolean): ActionableAdvice[] {
  const advices: ActionableAdvice[] = [];
  const dbg = (result.session.metrics.debug || ({} as any)) as any;
  const relief = dbg.transient_ratio || 0;
  const score = result.session.metrics.articulation || 0;

  if (score < 50) {
    advices.push({
      domain: "ATTAQUE",
      severity: relief < 0.3 ? "CRITICAL" : "WARNING",
      title: isFr ? "Jeu Écrase / Mou" : "Flattened Dynamics",
      instruction: isFr
        ? "Vos attaques manquent de 'front montant'. Le son arrive de manière trop graduelle, ce qui rend le rythme flou pour l'IA."
        : "Your attacks lack 'rising edge'. The sound arrives too gradually, making your timing blurry for the AI.",
      drill_hint: isFr ? "Action : Attaquez avec le bout du médiator" : "Action: Attack with the tip of the pick",
      icon: "🔨"
    });
  }

  return advices;
}

function generateStabilityAdvice(result: AnalysisResult, _level: DifficultyLevel, isFr: boolean): ActionableAdvice[] {
  const advices: ActionableAdvice[] = [];
  const stability = result.session.metrics.synchronization || 0;

  if (stability < 75) {
    advices.push({
      domain: "STABILITY",
      severity: "WARNING",
      title: isFr ? "Flux Interne Instable" : "Unstable Inner Flow",
      instruction: isFr
        ? "Votre 'Pocket Accuracy' est trop basse. Vous corrigez vos erreurs *après* coup au lieu de *prévoir* le temps."
        : "Your 'Pocket Accuracy' is too low. You are correcting errors *after* they happen instead of *predicting* the beat.",
      drill_hint: isFr ? "Action : Activez le mode Ancrage (Yeux fermés)" : "Action: Activate Anchor Mode (Eyes closed)",
      icon: "🧠"
    });
  }

  return advices;
}

function generateHarmonyAdvice(result: AnalysisResult, _level: DifficultyLevel, isFr: boolean): ActionableAdvice[] {
  const advices: ActionableAdvice[] = [];

  // ✅ Compat: certains contracts n'ont pas result.harmony
  const harmonyAny = (result as any).harmony;
  const gridValidation = (harmonyAny?.gridValidation as Array<{ expected: string; score: number }> | undefined) ?? [];

  if (gridValidation.length > 0) {
    const errors = gridValidation.filter(v => v.score < 70);
    if (errors.length > 0) {
      const worst = errors[0];
      advices.push({
        domain: "HARMONY",
        severity: "WARNING",
        title: isFr ? "Accord Étouffé" : "Muffled Chord",
        instruction: isFr
          ? `L'accord de ${worst.expected} manque de chant. Vérifiez que la paume de votre main gauche ne touche pas la corde aiguë.`
          : `The ${worst.expected} chord isn't singing. Check if your left palm is touching the high E string.`,
        drill_hint: isFr ? "Action : Arrondissez le poignet" : "Action: Round your wrist",
        icon: "🎸"
      });
      return advices;
    }
  }

  // ✅ Fallback si pas de gridValidation: on s'appuie sur le metric harmony si dispo
  const harmonyScore = (result.session.metrics as any)?.harmony;
  if (typeof harmonyScore === "number") {
    if (harmonyScore < 70) {
      advices.push({
        domain: "HARMONY",
        severity: "WARNING",
        title: isFr ? "Accord à clarifier" : "Chord clarity",
        instruction: isFr
          ? "L'accord manque de clarté. Vérifiez la pression des doigts et l'absence d'étouffement."
          : "The chord lacks clarity. Check finger pressure and avoid muting.",
        drill_hint: isFr ? "Action : Faites sonner corde par corde" : "Action: Ring strings one by one",
        icon: "🎸"
      });
      return advices;
    }
  }

  // ✅ Sinon SUCCESS
  advices.push({
    domain: "HARMONY",
    severity: "SUCCESS",
    title: isFr ? "Résonance Pure" : "Pure Resonance",
    instruction: isFr ? "Toutes les cordes vibrent librement." : "All strings vibrate freely.",
    drill_hint: isFr ? "Enchaînez avec plus de fluidité" : "Link with more fluidity",
    icon: "🎵"
  });

  return advices;
}

/**
 * ✅ NEW — CLEANLINESS (ghostNotes -> err/min + conseils)
 */
function generateCleanlinessAdvice(result: AnalysisResult, level: DifficultyLevel, isFr: boolean): ActionableAdvice[] {
  const advices: ActionableAdvice[] = [];

  const gpm = ghostsPerMinute(result);
  const thr = cleanlinessThreshold(level.id);

  // Si on n'a pas de duration, on ne tue pas l'UX : on skip
  if (!Number.isFinite(gpm)) return advices;

  if (gpm > thr) {
    const ratio = gpm / Math.max(1, thr);
    const sev = ratio >= 1.5 ? "CRITICAL" : "WARNING";

    advices.push({
      domain: "CLEANLINESS",
      severity: sev,
      title: isFr ? "Bruits parasites détectés" : "Unwanted noise detected",
      instruction: isFr
        ? `On détecte trop de notes fantômes (${gpm.toFixed(1)}/min). Ça brouille l'accord et fait chuter la propreté.`
        : `Too many ghost notes (${gpm.toFixed(1)}/min). This blurs chords and kills cleanliness.`,
      drill_hint: isFr
        ? "Action : relâchez légèrement la main gauche entre les changements + étouffez avec la paume droite"
        : "Action: slightly release fretting hand between changes + mute with right palm",
      icon: "🧽"
    });

    return advices;
  }

  advices.push({
    domain: "CLEANLINESS",
    severity: "SUCCESS",
    title: isFr ? "Jeu propre validé" : "Clean playing validated",
    instruction: isFr
      ? `Très peu de notes parasites (${gpm.toFixed(1)}/min).`
      : `Very few unwanted notes (${gpm.toFixed(1)}/min).`,
    drill_hint: isFr ? "Prêt pour accélérer / complexifier" : "Ready to speed up / add complexity",
    icon: "✨"
  });

  return advices;
}

/* ------------------------------------------------------------------
   2) EXPLANATIONS
------------------------------------------------------------------- */

function buildExplanationForAdvice(adv: ActionableAdvice, isFr: boolean): DiagnosticExplanation {
  const baseSignalId =
    adv.domain === "TIMING"
      ? (adv.icon === "🐇" ? "TIMING_RUSHING" : adv.icon === "🐢" ? "TIMING_DRAGGING" : "TIMING_OK")
      : adv.domain === "ATTAQUE"
      ? "ATTACK_SOFT_TRANSIENT"
      : adv.domain === "STABILITY"
      ? "STABILITY_POCKET_ACCURACY"
      : adv.domain === "HARMONY"
      ? "HARMONY_CHORD_CLARITY"
      : adv.domain === "CLEANLINESS"
      ? "CLEANLINESS_GHOST_NOTES"
      : `GEN_${adv.domain}`;

  const what = (() => {
    switch (adv.domain) {
      case "TIMING":
        return isFr
          ? "On mesure l’avance/retard moyen de tes attaques par rapport au tempo (déviation en millisecondes)."
          : "We measure your average early/late attack deviation against the tempo (in milliseconds).";
      case "ATTAQUE":
        return isFr
          ? "On mesure la netteté du départ du son (front montant / transitoire) : si c’est trop mou, le rythme devient flou."
          : "We measure how sharp your note onsets are (transient/rising edge). If too soft, rhythm becomes blurry.";
      case "STABILITY":
        return isFr
          ? "On mesure ta stabilité interne : est-ce que tu “prévois” le temps ou est-ce que tu corriges après coup."
          : "We measure inner stability: do you predict the beat, or correct after the fact.";
      case "HARMONY":
        return isFr
          ? "On mesure si l’accord sonne complet (cordes qui résonnent) et si la forme est tenue sans étouffement."
          : "We measure chord clarity (strings ringing) and whether fretting mutes a string.";
      case "CLEANLINESS":
        return isFr
          ? "On mesure la densité de notes parasites (ghost notes) par minute : bruits involontaires, cordes qui frottent, relâchements non contrôlés."
          : "We measure unwanted-note density (ghost notes) per minute: accidental noises, string rub, uncontrolled releases.";
      default:
        return isFr ? "On mesure un signal technique spécifique." : "We measure a specific technical signal.";
    }
  })();

  const why = (() => {
    switch (adv.domain) {
      case "TIMING":
        return isFr
          ? "Même avec les bons accords, un timing instable empêche la sensation de groove et limite la progression."
          : "Even with correct chords, unstable timing kills groove and blocks progress.";
      case "ATTAQUE":
        return isFr
          ? "Une attaque trop molle rend le placement rythmique imprécis et masque l’intention musicale."
          : "A too-soft attack blurs rhythmic placement and hides musical intent.";
      case "STABILITY":
        return isFr
          ? "Sans stabilité, tu dépenses ton cerveau à rattraper au lieu de jouer : fatigue + stagnation."
          : "Without stability, you spend your brain correcting instead of playing: fatigue + stagnation.";
      case "HARMONY":
        return isFr
          ? "Si une corde est étouffée, l’accord perd sa couleur : la musique “ne chante pas”."
          : "If a string is muted, the chord loses its color and won’t sing.";
      case "CLEANLINESS":
        return isFr
          ? "Les bruits parasites masquent l’accord et détruisent la lisibilité. Même si le rythme est bon, la musique paraît “sale”."
          : "Unwanted noise masks chords and kills clarity. Even with good rhythm, it sounds messy.";
      default:
        return isFr ? "Ce signal impacte directement le résultat." : "This signal directly impacts your result.";
    }
  })();

  return {
    signal_id: baseSignalId,
    domain: adv.domain,
    title: adv.title,
    what_is_measured: what,
    why_it_matters: why,
    severity: adv.severity
  };
}

/* ------------------------------------------------------------------
   3) PRIMARY PRESCRIPTION
------------------------------------------------------------------- */

function selectPrimaryPrescription(
  diagnostics: ActionableAdvice[],
  explanations: DiagnosticExplanation[],
  isFr: boolean
): MotorPrescription | undefined {
  const lang: "fr" | "en" = isFr ? "fr" : "en";

  const candidates = diagnostics
    .filter(d => d.severity !== "SUCCESS")
    .map(d => {
      const exp =
        explanations.find(e => e.title === d.title && e.domain === d.domain) ||
        buildExplanationForAdvice(d, isFr);
      return buildMotorPrescription(d, exp, lang);
    })
    .filter(Boolean) as MotorPrescription[];

  if (candidates.length === 0) return undefined;

  candidates.sort((a, b) => {
    const aForce = a.force_mode ? 0 : 1;
    const bForce = b.force_mode ? 0 : 1;
    if (aForce !== bForce) return aForce - bForce;
    return (a.priority ?? 999) - (b.priority ?? 999);
  });

  return candidates[0];
}

/* ------------------------------------------------------------------
   4) MAIN
------------------------------------------------------------------- */

export function generateAllCoachingAdvice(
  result: AnalysisResult,
  levelId: number,
  lang: "fr" | "en" = "fr"
): CoachingVerdict {
  const isFr = lang === "fr";
  const level = { name: "Niveau " + levelId, id: levelId } as DifficultyLevel;

  const analysisCount = parseInt(localStorage.getItem("gsc_analysis_count") || "0");
  const isPremium = localStorage.getItem("gsc_is_premium") === "true";

  const visibleTabs = getVisibleTabsForLevel(levelId, analysisCount, isPremium);

  const domainToTab: Record<string, string> = {
    TIMING: "rhythm",
    HARMONY: "harmony",
    STABILITY: "synchronization",
    POSTURE: "posture",
    NUANCE: "nuance",
    TONE: "tone",
    CLEANLINESS: "cleanliness",
    ATTAQUE: "articulation"
  };

  const allDiagnostics: ActionableAdvice[] = [
    ...generateTimingAdvice(result, level, isFr),
    ...generateStabilityAdvice(result, level, isFr),
    ...generateHarmonyAdvice(result, level, isFr),
    ...generateAttackAdvice(result, level, isFr),
    ...generateCleanlinessAdvice(result, level, isFr)
  ];

  const filteredDiagnostics = allDiagnostics.filter(adv => {
    const tabId = domainToTab[adv.domain];
    return !tabId || visibleTabs.includes(tabId);
  });

  const severityOrder = { CRITICAL: 0, WARNING: 1, SUCCESS: 2 } as const;
  type SeverityKey = keyof typeof severityOrder;

  filteredDiagnostics.sort((a, b) => {
    const av = severityOrder[a.severity as SeverityKey] ?? 99;
    const bv = severityOrder[b.severity as SeverityKey] ?? 99;
    return av - bv;
  });

  const prioritizedAdvices: string[] = [];
  filteredDiagnostics
    .filter(d => d.severity !== "SUCCESS")
    .slice(0, 3)
    .forEach(d => prioritizedAdvices.push(`${d.title}: ${d.drill_hint}`));

  const explanations: DiagnosticExplanation[] = filteredDiagnostics.map(d => buildExplanationForAdvice(d, isFr));
  const primaryPrescription = selectPrimaryPrescription(filteredDiagnostics, explanations, isFr);

  return {
    severity: level.name,
    tone: levelId <= 2 ? "BENEVOLENT" : levelId >= 7 ? "ELITE" : "STANDARD",
    prioritizedAdvices,
    diagnostics: filteredDiagnostics,
    explanations,
    primaryPrescription
  };
}
